import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  getLeaves,
  createLeaveApplication,
  updateLeaveApplication,
  deleteLeaveApplication,
  approveLeave, // Assuming approveLeave is already defined in your leave.service.js
} from "../../services/leave.services"; // Service functions for leave operations

const initialState = {
  calendarState: {
    leaveRequests: [], // Leave requests fetched from the backend
    currentMonth: dayjs().format("YYYY-MM-DD"),
    selectedDates: [], // Store selected dates for leave application
    reason: "",
    name: "",
    events: [], // Active events
  },
};

// Async Thunks to handle asynchronous API requests
export const fetchLeaveRequests = createAsyncThunk(
  "calendar/fetchLeaveRequests",
  async () => {
    const response = await getLeaves();
    return response.data;
  }
);

export const submitLeaveRequest = createAsyncThunk(
  "calendar/submitLeaveRequest",
  async (leaveData, { dispatch }) => {
    const response = await createLeaveApplication(leaveData);
    dispatch(fetchLeaveRequests());
    return response.data;
  }
);

export const modifyLeaveRequest = createAsyncThunk(
  "calendar/modifyLeaveRequest",
  async ({ id, leaveData }, { dispatch }) => {
    const response = await updateLeaveApplication(id, leaveData);
    dispatch(fetchLeaveRequests());
    // response.data);

    return response.data;
  }
);

export const deleteLeaveRequest = createAsyncThunk(
  "calendar/deleteLeaveRequest",
  async (id, { dispatch }) => {
    const response = await deleteLeaveApplication(id);
    dispatch(fetchLeaveRequests());
    return response.data;
  }
);

// Approve or deny leave request service call
export const approveLeaveRequest = createAsyncThunk(
  "calendar/approveLeaveRequest",
  async ({ id, status }, { dispatch, getState }) => {
    const response = await approveLeave(id, status); // Call approveLeave service

    // Directly update the leave request in the store
    dispatch({
      type: "calendar/updateLeaveRequestStatus",
      payload: { id, status: response.status },
    });
    return { id, status: response.status }; // Return updated leave request data after approval
  }
);

// Create slice
const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    setCurrentMonth: (state, action) => {
      state.calendarState.currentMonth = action.payload;
    },

    toggleSelectedDate: (state, action) => {
      const date = action.payload;
      if (state.calendarState.selectedDates.includes(date)) {
        state.calendarState.selectedDates =
          state.calendarState.selectedDates.filter((d) => d !== date);
      } else {
        state.calendarState.selectedDates.push(date);
      }
    },

    clearSelectedDates: (state) => {
      state.calendarState.selectedDates = [];
    },

    setReason: (state, action) => {
      state.calendarState.reason = action.payload;
    },

    setName: (state, action) => {
      state.calendarState.name = action.payload;
    },

    addEvent: (state, action) => {
      state.calendarState.events.push({
        ...action.payload,
        status: "Pending", // Default status for new events
      });
    },

    removeEvent: (state, action) => {
      state.calendarState.events = state.calendarState.events.filter(
        (event) => event.id !== action.payload
      );
    },
    updateLeaveRequestStatus: (state, action) => {
      const { id, status } = action.payload;
      const leaveRequestIndex =
        state.calendarState.approveLeaveRequest.findIndex(
          (request) => request.id === id
        );

      if (leaveRequestIndex !== -1) {
        state.calendarState.approveLeaveRequest[leaveRequestIndex].status =
          status;

        if (status !== "PENDING") {
          const updatedRequest =
            state.calendarState.approveLeaveRequest[leaveRequestIndex];
          state.calendarState.approveLeaveRequest.splice(leaveRequestIndex, 1);
          state.calendarState.events.push(updatedRequest);
        }
      }
    },

    addLeaveRequest: (state, action) => {
      const newRequest = action.payload;
      state.calendarState.leaveRequests.push(newRequest);
      state.calendarState.events.push({
        ...newRequest,
        status: "Pending",
      });
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchLeaveRequests.fulfilled, (state, action) => {
        state.calendarState.leaveRequests = action.payload;
      })
      .addCase(submitLeaveRequest.fulfilled, (state, action) => {
        state.calendarState.leaveRequests.push(action.payload);
      })
      .addCase(modifyLeaveRequest.fulfilled, (state, action) => {
        const updatedRequestIndex = state.calendarState.leaveRequests.findIndex(
          (req) => req.id === action.payload.id
        );
        if (updatedRequestIndex !== -1) {
          state.calendarState.leaveRequests[updatedRequestIndex] =
            action.payload;
        }
      })
      .addCase(deleteLeaveRequest.fulfilled, (state, action) => {
        state.calendarState.leaveRequests =
          state.calendarState.leaveRequests.filter(
            (req) => req.id !== action.payload.id
          );
      })
      .addCase(approveLeaveRequest.fulfilled, (state, action) => {
        const { id, status } = action.payload; // Get the id and updated status from the response
        const leaveRequestIndex = state.calendarState.leaveRequests.findIndex(
          (req) => req.id === id
        );

        if (leaveRequestIndex !== -1) {
          state.calendarState.leaveRequests[leaveRequestIndex].status = status;
          // Optionally, add the updated request to events if necessary
          const updatedRequest =
            state.calendarState.leaveRequests[leaveRequestIndex];
          state.calendarState.leaveRequests.splice(leaveRequestIndex, 1);
          state.calendarState.events.push(updatedRequest);
        }
      });
  },
});

export const {
  setCurrentMonth,
  toggleSelectedDate,
  clearSelectedDates,
  setReason,
  setName,
  addEvent,
  removeEvent,
  updateLeaveRequestStatus,
  addLeaveRequest,
} = calendarSlice.actions;

export default calendarSlice.reducer;
